import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  DatePicker,
  Statistic,
  Row,
  Col,
  Spin,
  Badge,
  notification,
  Tooltip
} from 'antd';
import {
  ClockCircleOutlined,
  TeamOutlined,
  CoffeeOutlined,
  EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import * as workService from './workTrackingService';

const TeamDashboard = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [teamData, setTeamData] = useState(null);

  // Helper function to format time to local timezone
  const formatLocalTime = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return 'Invalid';
    }
  };

  useEffect(() => {
    fetchTeamData();

    // Auto-refresh every 2 minutes
    const interval = setInterval(() => fetchTeamData(true), 120000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const fetchTeamData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const dateStr = selectedDate.format('YYYY-MM-DD');
      const res = await workService.getTeamDashboard(dateStr);
      setTeamData(res.data);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to load team data'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'on_break': 'warning',
      'idle': 'error',
      'clocked_out': 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      'active': 'Active',
      'on_break': 'On Break',
      'idle': 'Idle',
      'clocked_out': 'Clocked Out'
    };
    return texts[status] || status;
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '-';
    const minutes = moment().diff(moment(timestamp), 'minutes');

    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 min ago';
    if (minutes < 60) return `${minutes} mins ago`;

    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour ago';
    return `${hours} hours ago`;
  };

  const columns = [
    {
      title: 'Staff',
      dataIndex: 'firstName',
      key: 'name',
      render: (text, record) => (
        <strong>{`${record.firstName} ${record.lastName}`}</strong>
      ),
      sorter: (a, b) => `${a.firstName}`.localeCompare(`${b.firstName}`),
    },
    {
      title: 'Status',
      dataIndex: 'current_status',
      key: 'status',
      render: (status) => (
        <Badge
          status={getStatusColor(status)}
          text={<strong>{getStatusText(status)}</strong>}
        />
      ),
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'On Break', value: 'on_break' },
        { text: 'Idle', value: 'idle' },
      ],
      onFilter: (value, record) => record.current_status === value,
    },
    {
      title: 'Clock In',
      dataIndex: 'clock_in_time',
      key: 'clock_in',
      render: (time) => formatLocalTime(time),
    },
    {
      title: 'Hours Worked',
      dataIndex: 'minutes_worked_today',
      key: 'hours',
      render: (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
      },
      sorter: (a, b) => a.minutes_worked_today - b.minutes_worked_today,
    },
    {
      title: 'Break Time',
      dataIndex: 'total_break_minutes',
      key: 'break',
      render: (minutes) => {
        if (!minutes || minutes === 0) return '-';
        return `${minutes}m`;
      },
    },
    {
      title: 'Current Activity',
      dataIndex: 'current_activity_category',
      key: 'activity',
      render: (activity) => activity ? <Tag color="blue">{activity}</Tag> : '-',
    },
    {
      title: 'Last Activity',
      dataIndex: 'minutes_since_last_activity',
      key: 'last_activity',
      render: (minutes, record) => {
        if (!record.last_activity_time) return '-';

        let color = 'green';
        if (minutes > 30) color = 'red';
        else if (minutes > 15) color = 'orange';

        return (
          <Tooltip title={moment(record.last_activity_time).format('hh:mm A')}>
            <Tag color={color}>{getTimeAgo(record.last_activity_time)}</Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.minutes_since_last_activity || 0) - (b.minutes_since_last_activity || 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => history.push(`/work-tracking/staff-details/${record.staff_id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  if (loading && !teamData) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="team-dashboard">
      <Card
        title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}><TeamOutlined style={{ marginRight: 8 }} />Team Work Dashboard</span>}
        headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        extra={
          <Space>
            <DatePicker
              value={selectedDate}
              onChange={(date) => setSelectedDate(date || moment())}
              format="DD-MM-YYYY"
              allowClear={false}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchTeamData()}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Total Staff"
                value={teamData?.summary?.total_staff || 0}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Active"
                value={teamData?.summary?.active_count || 0}
                valueStyle={{ color: '#52c41a' }}
                suffix={
                  <Tag color="success">
                    {Math.round((teamData?.summary?.active_count / teamData?.summary?.total_staff) * 100) || 0}%
                  </Tag>
                }
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="On Break"
                value={teamData?.summary?.on_break_count || 0}
                prefix={<CoffeeOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Idle"
                value={teamData?.summary?.idle_count || 0}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Staff Table */}
        <Table
          columns={columns}
          dataSource={teamData?.active_staff || []}
          rowKey="staff_id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Activity Summary */}
      {teamData?.activity_summary?.length > 0 && (
        <Card title="Activity Summary" style={{ marginTop: 16 }}>
          <Row gutter={[16, 16]}>
            {teamData.activity_summary.map((staff) => (
              <Col xs={24} sm={12} md={8} key={staff.staff_id}>
                <Card
                  size="small"
                  title={`${staff.firstName} ${staff.lastName}`}
                  extra={<Tag color="blue">{staff.total_activities} activities</Tag>}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div>
                      <ClockCircleOutlined /> Total Time: {Math.round(staff.total_duration || 0)} mins
                    </div>
                    {staff.last_activity_time && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Last activity: {moment(staff.last_activity_time).format('hh:mm A')}
                      </div>
                    )}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}
    </div>
  );
};

export default TeamDashboard;
